/**
 * HustleRoundupLoginPage — email/password login for the Sprint 2 build/test.
 *
 * The pod-local `users` collection is seeded with:
 *   roundup-tester@example.com / Roundup!Test2026  (role=host)
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Video, LogIn } from "lucide-react";
import { apiFetch, saveToken, fetchMe } from "@/lib/api";

export default function HustleRoundupLoginPage() {
  const [email, setEmail]       = useState("roundup-tester@example.com");
  const [password, setPassword] = useState("Roundup!Test2026");
  const [busy, setBusy]         = useState(false);
  const navigate                = useNavigate();
  const [params]                = useSearchParams();
  const next                    = params.get("next") || "/meet";

  useEffect(() => {
    (async () => {
      const me = await fetchMe();
      if (me?.id) navigate(next, { replace: true });
    })();
  }, [navigate, next]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const data = await apiFetch("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      saveToken(data.token);
      toast.success(`Welcome, ${data.user?.name || data.user?.email}`);
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="hustleroundup-login"
      className="min-h-screen bg-stone-950 text-white flex items-center justify-center px-6"
      style={{
        backgroundImage:
          "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(59,130,246,0.10), transparent 45%)",
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-stone-900/70 border border-stone-800 rounded-2xl p-8 backdrop-blur-xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <Video className="w-8 h-8 text-emerald-400" />
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">HustleRoundup</div>
            <div className="text-xl font-bold">Sign in</div>
          </div>
        </div>

        <label className="text-[11px] uppercase tracking-widest text-stone-400 block mb-1.5">Email</label>
        <input
          type="email"
          data-testid="hustleroundup-login-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg bg-stone-950 border border-stone-800 focus:border-emerald-500 outline-none text-sm mb-4"
        />
        <label className="text-[11px] uppercase tracking-widest text-stone-400 block mb-1.5">Password</label>
        <input
          type="password"
          data-testid="hustleroundup-login-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-3 py-2.5 rounded-lg bg-stone-950 border border-stone-800 focus:border-emerald-500 outline-none text-sm mb-6"
        />
        <button
          type="submit"
          data-testid="hustleroundup-login-submit"
          disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-stone-950 font-semibold text-sm disabled:opacity-60"
        >
          <LogIn className="w-4 h-4" /> {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="mt-4 text-[11px] text-stone-500 leading-relaxed">
          Test host: <code className="text-emerald-400/80">roundup-tester@example.com</code>{" / "}
          <code className="text-emerald-400/80">Roundup!Test2026</code>.<br />
          <Link to="/" className="underline hover:text-white">Back to landing</Link>
        </p>
      </form>
    </div>
  );
}
